import axios from "axios";
import { getSupabase } from "@/lib/supabase";
import { requestChallenge } from "@/lib/challenge";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use(async (cfg) => {
  const sb = getSupabase();
  if (sb) {
    const { data } = await sb.auth.getSession();
    const token = data.session?.access_token;
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

const NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/me"];
let refreshing = null;

api.interceptors.response.use(undefined, async (e) => {
  const cfg = e?.config;
  if (e?.response?.status === 401 && cfg && !cfg._retried && !getSupabase() && !NO_REFRESH.some((p) => cfg.url?.endsWith(p))) {
    cfg._retried = true;
    try {
      refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
      await refreshing;
      return api(cfg);
    } catch {
      /* fall through */
    }
  }
  if (e?.response?.status === 428 && e.response.data?.detail === "challenge_required" && cfg && !cfg._challenged) {
    cfg._challenged = true;
    try {
      const token = await requestChallenge();
      cfg.headers = { ...cfg.headers, "X-Turnstile-Token": token };
      return api(cfg);
    } catch {
      /* fall through */
    }
  }
  if (e?.response?.status === 403 && e.response.data?.detail === "onboarding_required" && window.location.pathname !== "/onboarding") {
    window.location.assign("/onboarding");
  }
  return Promise.reject(e);
});

export const newRequestId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const errMsg = (e, fallback = "Something went wrong. Please retry.") => {
  if (axios.isCancel(e) || e?.code === "ERR_CANCELED") return "Cancelled.";
  if (e?.code === "ECONNABORTED") return "The request timed out. Please retry.";
  if (!e?.response) return "Network error — check your connection and retry.";
  const d = e.response.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (typeof x?.msg === "string" ? x.msg.replace(/^Value error, /, "") : "")).filter(Boolean);
    return msgs.length ? msgs.join(" ") : "Some input looks invalid. Please check and try again.";
  }
  return fallback;
};

export const blobErrMsg = async (e, fallback) => {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const j = JSON.parse(await data.text());
      if (typeof j.detail === "string") return j.detail;
    } catch {
      /* ignore */
    }
  }
  return errMsg(e, fallback);
};
